// utils/bossQuestUtils.js
// Pure utilities: no React hooks here.
// Generates a weekly Boss Quest from the last 7 days of daily quests.

import { callOpenAI, OPENAI_MODELS } from "../api/openai";
import { getDailyQuestRow, getProfile } from "../api/supabaseData";
import { getTitleForLevel } from "./levelUtils";
import {
  buildHunterProfileContext,
  buildMemoryContext,
  buildSystemPrompt,
  getLevelDescriptor,
} from "./systemPrompts";
import { buildSystemMemory } from "./systemMemory";
import { getWeekIdForTimeZone, normalizeTimeZone } from "./timezoneUtils";

// --- date helpers (local time, not UTC) ---
const pad2 = (n) => String(n).padStart(2, "0");
export const localDateString = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

// ISO week id as YYYY-WW (e.g., 2025-33)
export const getWeekId = (d = new Date(), timeZone = null) => {
  if (timeZone) {
    return getWeekIdForTimeZone(normalizeTimeZone(timeZone), d);
  }
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  // Thursday in current week determines the year/week
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
  const week1 = new Date(date.getFullYear(), 0, 4);
  const week =
    1 +
    Math.round(
      ((date.getTime() - week1.getTime()) / 86400000 -
        3 +
        ((week1.getDay() + 6) % 7)) /
        7
    );
  return `${date.getFullYear()}-${pad2(week)}`;
};

const getLevelTitle = (level) => {
  return getTitleForLevel(level);
};

const cleanBossLine = (value) =>
  String(value || "")
    .replace(/\s+/g, " ")
    .replace(/^[-*]\s*/, "")
    .trim();

const truncate = (value, limit) => cleanBossLine(value).slice(0, limit);

function extractBossSummaryAndChecklist(desc = "") {
  const raw = cleanBossLine(desc);
  if (!raw) {
    return {
      summary: "Complete the weekly boss objective below.",
      checklist: [],
    };
  }

  const targetMatch = raw.match(/^(.*?)(?:targets?:)\s*(.*)$/i);
  if (targetMatch) {
    const summary = cleanBossLine(targetMatch[1]) || "Complete the weekly boss objective below.";
    const checklist = targetMatch[2]
      .split(/\s*;\s*/)
      .map((item) => truncate(item, 88))
      .filter(Boolean)
      .slice(0, 4);

    return {
      summary,
      checklist,
    };
  }

  const pieces = raw
    .split(/\s*;\s*|\.\s+(?=[A-Z0-9])/)
    .map((item) => truncate(item, 88))
    .filter(Boolean);

  if (pieces.length <= 1) {
    return {
      summary: raw,
      checklist: [],
    };
  }

  return {
    summary: pieces[0],
    checklist: pieces.slice(1, 5),
  };
}

function normalizeBossQuestPayload(data, xp, fallbackTitle, fallbackDesc, source) {
  const title = truncate(data?.title || fallbackTitle || "Weekly Boss Challenge", 60);
  const summary = truncate(data?.desc || fallbackDesc || "Complete a consolidated weekly challenge.", 180);
  const checklistSource = Array.isArray(data?.checklist) ? data.checklist : [];
  const checklist = checklistSource
    .map((item) => truncate(item, 88))
    .filter(Boolean)
    .slice(0, 4);
  const acceptance = truncate(data?.acceptance || "", 120);

  return {
    title,
    desc: summary,
    checklist,
    acceptance,
    xp,
    source,
  };
}

export function getBossQuestPresentation(quest) {
  const summary = truncate(quest?.desc || "", 220);
  const acceptance = truncate(quest?.acceptance || "", 120);
  const checklist = Array.isArray(quest?.checklist)
    ? quest.checklist.map((item) => truncate(item, 88)).filter(Boolean).slice(0, 4)
    : [];

  if (checklist.length) {
    return {
      summary: summary || "Complete the weekly boss objective below.",
      checklist,
      acceptance,
    };
  }

  const extracted = extractBossSummaryAndChecklist(summary);
  return {
    summary: extracted.summary,
    checklist: extracted.checklist,
    acceptance,
  };
}

// Fetch last 7 days of daily quests ending "yesterday"
export async function fetchLast7DaysQuests(uid, endDate = new Date()) {
  const days = [];
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  for (let i = 1; i <= 7; i++) {
    const d = new Date(end);
    d.setDate(d.getDate() - i); // yesterday back to -7
    days.push(localDateString(d));
  }

  const all = [];
  for (const ds of days) {
    const row = await getDailyQuestRow(uid, ds);
    const qs = row?.quests || [];
    for (const q of qs) all.push({ ...q, __date: ds });
  }
  return all;
}

// Build a compact summary of the week to guide the boss quest prompt
export function summarizeWeek(quests) {
  const byWord = new Map();
  let completed = 0,
    total = 0,
    xpSum = 0,
    xpCount = 0;

  for (const q of quests) {
    total++;
    if (q.completed) completed++;
    const xp = Number(q.xp) || 0;
    if (xp) {
      xpSum += xp;
      xpCount++;
    }
    const text = [q.title, q.desc].filter(Boolean).join(" ").toLowerCase();
    const words = text.split(/[^a-z0-9]+/).filter((w) => w.length >= 3);
    for (const w of words) byWord.set(w, (byWord.get(w) || 0) + 1);
  }

  const themes = Array.from(byWord.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([term, count]) => ({ term, count }));

  const avgDailyXP = xpCount ? Math.round(xpSum / xpCount) : 0;

  return {
    themes,
    completedCount: completed,
    totalCount: total,
    avgDailyXP,
  };
}

// Tougher than dailies; scalable with level + last week's performance
export function computeBossXP(level, summary) {
  const base = 50 + (Number(level) || 1) * 10;
  const streakBonus = Math.floor((summary.completedCount || 0) / 3) * 10;
  const xp = Math.max(
    120,
    Math.min(
      600,
      base +
        (summary.avgDailyXP || 0) * 2 +
        (summary.completedCount || 0) * 3 +
        streakBonus
    )
  );
  return Math.round(xp);
}

// High-level: generate boss quest JSON from last-week history.
// Returns { title, desc, xp } or null if there's no history.
export async function generateBossQuestFromHistory(uid, userLevel) {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  const quests = await fetchLast7DaysQuests(uid, yesterday);
  if (!quests.length) return null;

  const profile = await getProfile(uid);
  const memory = await buildSystemMemory(uid);
  const summary = summarizeWeek(quests);
  const prompt = buildSystemPrompt({
    contextLines: [
      `Create one weekly Boss Quest for a hunter at ${getLevelDescriptor(userLevel)}.`,
      buildHunterProfileContext(profile),
      `Weekly performance summary JSON: ${JSON.stringify(summary)}`,
      buildMemoryContext(memory),
    ],
    instructionLines: [
      "Integrate last week's learning and raise the difficulty above normal daily quests.",
      "Use the hunter record to make the weekly directive feel personal and historically grounded.",
      "If the hunter keeps repeating the same failures, tighten the quest around that weakness.",
      "If the hunter has defeated bosses consistently, escalate expectations.",
      "Make it realistic, measurable, and time-boxed to this week.",
      "Use plain everyday language a normal user can understand immediately.",
      "Avoid technical jargon, abbreviations, or formula-style wording.",
      "Give one short summary sentence and 2 to 4 short checklist items.",
      "Include one clear acceptance criterion.",
      "Keep it demanding but achievable.",
    ],
    outputLines: [
      'Return JSON only in this shape: {"title":"...", "desc":"...", "checklist":["..."], "acceptance":"..."}',
      "Title max 60 characters.",
      "Summary max 140 characters.",
      "Each checklist item max 80 characters.",
      "Acceptance max 100 characters.",
      "No markdown, no code fences, no extra commentary.",
    ],
  });

  const out = await callOpenAI(prompt, 256, 0.7, { model: OPENAI_MODELS.ANALYSIS });

  let data;
  try {
    data = JSON.parse(out?.result || "{}");
  } catch {
    data = { title: "Weekly Boss Challenge", desc: out?.result || fallbackBossSummary(summary) };
  }

  const xp = computeBossXP(userLevel, summary);
  return normalizeBossQuestPayload(
    data,
    xp,
    "Weekly Boss Challenge",
    fallbackBossSummary(summary),
    "ai"
  );
}

function fallbackBossSummary(summary) {
  const topThemes = (summary?.themes || [])
    .slice(0, 3)
    .map((entry) => entry.term)
    .filter(Boolean);

  if (!topThemes.length) {
    return "Finish one focused weekly challenge built from your recent quests.";
  }

  return `Complete one focused weekly challenge around ${topThemes.join(", ")}.`;
}
